import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent } from "react";
import { appConfig, type AgentId } from "../config/appConfig";
import { maskMemberAccountForDisplay } from "../utils/memberAccountDisplay";
import "./admin.css";

type ClaimStatus = "pending" | "completed" | "rejected";
type StatusFilter = "all" | ClaimStatus;
type ExportAgentFilter = "all" | AgentId;

type Claim = {
  claimId: string;
  memberAccount: string;
  prizePoints: number;
  status: ClaimStatus;
  createdAt: string;
  processedAt: string | null;
  notes: string | null;
};

type AgentStats = {
  agentId: string;
  applicantCount: number;
  pendingCount: number;
  completedCount: number;
  rejectedCount: number;
  completedPoints: number;
  claims: Claim[];
};

type OverviewStats = Omit<AgentStats, "agentId" | "rejectedCount" | "claims">;

type AgentStatsResponse = {
  ok: boolean;
  agents?: AgentStats[];
  totals?: OverviewStats;
};

type AdminVerificationResult =
  | { status: "success"; data: AgentStatsResponse }
  | { status: "unauthorized" }
  | { status: "unavailable" };

const SESSION_PASSWORD_KEY = "914go-admin-password";
const SESSION_LAST_ACTIVITY_KEY = "914go-admin-last-activity";
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;
const IDLE_CHECK_INTERVAL_MS = 1000;
const IDLE_LOGOUT_MESSAGE = "閒置超過 5 分鐘，請重新登入。";
const INVALID_SESSION_MESSAGE = "登入狀態已失效，請重新登入。";
const INVALID_PASSWORD_MESSAGE = "管理密碼錯誤，請重新輸入。";
const AUTH_SERVICE_ERROR_MESSAGE = "無法連線到後台驗證服務，請稍後再試。";

const STATUS_LABELS: Record<ClaimStatus, string> = {
  pending: "待處理",
  completed: "已完成",
  rejected: "已拒絕",
};

const EMPTY_OVERVIEW: OverviewStats = {
  applicantCount: 0,
  pendingCount: 0,
  completedCount: 0,
  completedPoints: 0,
};

function emptyAgentStats(agentId: AgentId): AgentStats {
  return {
    agentId,
    applicantCount: 0,
    pendingCount: 0,
    completedCount: 0,
    rejectedCount: 0,
    completedPoints: 0,
    claims: [],
  };
}

function readSessionPassword() {
  try {
    return sessionStorage.getItem(SESSION_PASSWORD_KEY) ?? "";
  } catch {
    return "";
  }
}

function saveSessionPassword(password: string) {
  try {
    sessionStorage.setItem(SESSION_PASSWORD_KEY, password);
  } catch {
    // 無法使用 sessionStorage 時，密碼仍只保留在目前 React 記憶體。
  }
}

function clearSessionPassword() {
  try {
    sessionStorage.removeItem(SESSION_PASSWORD_KEY);
  } catch {
    // sessionStorage 不可用時不需額外處理。
  }
}

function readSessionLastActivity() {
  try {
    const value = Number(sessionStorage.getItem(SESSION_LAST_ACTIVITY_KEY));
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

function saveSessionLastActivity(value: number) {
  try {
    sessionStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(value));
  } catch {
    // 無法使用 sessionStorage 時，仍可由目前分頁的記憶體計時。
  }
}

function clearSessionLastActivity() {
  try {
    sessionStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
  } catch {
    // sessionStorage 不可用時不需額外處理。
  }
}

export async function verifyAdminPassword(
  password: string,
  request: typeof fetch = fetch,
): Promise<AdminVerificationResult> {
  try {
    const response = await request("/api/admin/agents", {
      headers: { "X-Admin-Password": password },
    });

    if (response.status === 401 || response.status === 403) {
      return { status: "unauthorized" };
    }

    if (!response.ok) {
      return { status: "unavailable" };
    }

    const data = await response.json() as AgentStatsResponse;

    if (data.ok !== true) {
      return { status: "unavailable" };
    }

    return { status: "success", data };
  } catch {
    return { status: "unavailable" };
  }
}

function formatTaiwanTime(value: string) {
  if (!value) return "—";

  const hasTimeZone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value);
  const normalizedValue = hasTimeZone
    ? value
    : `${value.replace(" ", "T")}Z`;
  const date = new Date(normalizedValue);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("zh-TW", {
    dateStyle: "medium",
    timeStyle: "medium",
    hour12: false,
    timeZone: "Asia/Taipei",
  }).format(date);
}

function taiwanDateKey(value: string) {
  if (!value) return "";

  const hasTimeZone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value);
  const normalizedValue = hasTimeZone
    ? value
    : `${value.replace(" ", "T")}Z`;
  const date = new Date(normalizedValue);

  if (Number.isNaN(date.getTime())) return "";

  const parts = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "Asia/Taipei",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return `${values.year}-${values.month}-${values.day}`;
}

function csvCell(value: string | number) {
  return `"${String(value).replace(/"/g, '""')}"`;
}

export function AdminApp() {
  const [passwordInput, setPasswordInput] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [agentStats, setAgentStats] = useState<Partial<Record<AgentId, AgentStats>>>({});
  const [overview, setOverview] = useState<OverviewStats>(EMPTY_OVERVIEW);
  const [selectedAgentId, setSelectedAgentId] = useState<AgentId | null>(null);
  const [expandedClaimId, setExpandedClaimId] = useState<string | null>(null);
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [exportAgentId, setExportAgentId] = useState<ExportAgentFilter>("all");
  const [exportStatus, setExportStatus] = useState<StatusFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [memberSearchInput, setMemberSearchInput] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [notesDrafts, setNotesDrafts] = useState<Record<string, string>>({});
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [updatingClaimId, setUpdatingClaimId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const lastActivityRef = useRef(0);
  const adminSessionIdRef = useRef(0);
  const verificationIdRef = useRef(0);
  const restorationStartedRef = useRef(false);
  const isAuthenticatedRef = useRef(false);

  const exportClaims = useMemo(() => appConfig.nfcAgents.flatMap((agent) => (
    (agentStats[agent.id]?.claims ?? []).map((claim) => ({ agent, claim }))
  )).filter(({ agent, claim }) => {
    const claimDate = taiwanDateKey(claim.createdAt);

    return (exportAgentId === "all" || agent.id === exportAgentId)
      && (exportStatus === "all" || claim.status === exportStatus)
      && (!exportStartDate || claimDate >= exportStartDate)
      && (!exportEndDate || claimDate <= exportEndDate);
  }).sort((left, right) => (
    new Date(right.claim.createdAt).getTime() - new Date(left.claim.createdAt).getTime()
  )), [agentStats, exportAgentId, exportEndDate, exportStartDate, exportStatus]);

  const clearAdminSession = useCallback((message: string) => {
    verificationIdRef.current += 1;
    adminSessionIdRef.current += 1;
    lastActivityRef.current = 0;
    clearSessionPassword();
    clearSessionLastActivity();
    isAuthenticatedRef.current = false;
    setAdminPassword("");
    setPasswordInput("");
    setIsAuthenticated(false);
    setAgentStats({});
    setOverview(EMPTY_OVERVIEW);
    setSelectedAgentId(null);
    setExpandedClaimId(null);
    setExportStartDate("");
    setExportEndDate("");
    setExportAgentId("all");
    setExportStatus("all");
    setStatusFilter("all");
    setMemberSearchInput("");
    setMemberSearch("");
    setNotesDrafts({});
    setIsVerifying(false);
    setSummaryLoading(false);
    setUpdatingClaimId(null);
    setError(message);
  }, []);

  const rejectAuthorization = useCallback(() => {
    clearAdminSession(INVALID_PASSWORD_MESSAGE);
  }, [clearAdminSession]);

  const applyAgentStats = useCallback((data: AgentStatsResponse) => {
    const nextAgentStats: Partial<Record<AgentId, AgentStats>> = {};

    for (const stats of data.agents ?? []) {
      const configuredAgent = appConfig.nfcAgents.find((agent) => agent.id === stats.agentId);

      if (configuredAgent) {
        nextAgentStats[configuredAgent.id] = stats;
      }
    }

    setAgentStats(nextAgentStats);
    setOverview(data.totals ?? EMPTY_OVERVIEW);
    setNotesDrafts(Object.fromEntries(
      (data.agents ?? []).flatMap((stats) => stats.claims ?? [])
        .map((claim) => [claim.claimId, claim.notes ?? ""]),
    ));
  }, []);

  const authenticate = useCallback(async (
    candidatePassword: string,
    restoredLastActivity?: number,
  ) => {
    const verificationId = verificationIdRef.current + 1;
    verificationIdRef.current = verificationId;
    isAuthenticatedRef.current = false;
    lastActivityRef.current = 0;
    clearSessionPassword();
    clearSessionLastActivity();
    setAdminPassword("");
    setIsAuthenticated(false);
    setAgentStats({});
    setOverview(EMPTY_OVERVIEW);
    setNotesDrafts({});
    setError("");
    setIsVerifying(true);

    const result = await verifyAdminPassword(candidatePassword);

    if (verificationId !== verificationIdRef.current) return;

    if (result.status === "unauthorized") {
      clearAdminSession(INVALID_PASSWORD_MESSAGE);
      return;
    }

    if (result.status === "unavailable") {
      clearAdminSession(AUTH_SERVICE_ERROR_MESSAGE);
      return;
    }

    if (
      restoredLastActivity !== undefined
      && Date.now() - restoredLastActivity >= IDLE_TIMEOUT_MS
    ) {
      clearAdminSession(IDLE_LOGOUT_MESSAGE);
      return;
    }

    const timestamp = restoredLastActivity ?? Date.now();
    applyAgentStats(result.data);
    adminSessionIdRef.current += 1;
    lastActivityRef.current = timestamp;
    saveSessionPassword(candidatePassword);
    saveSessionLastActivity(timestamp);
    setAdminPassword(candidatePassword);
    setPasswordInput(candidatePassword);
    isAuthenticatedRef.current = true;
    setIsVerifying(false);
    setIsAuthenticated(true);
  }, [applyAgentStats, clearAdminSession]);

  const loadAgentStats = useCallback(async () => {
    if (!adminPassword || !isAuthenticatedRef.current) return;

    const sessionId = adminSessionIdRef.current;
    setSummaryLoading(true);
    setError("");

    try {
      const response = await fetch("/api/admin/agents", {
        headers: { "X-Admin-Password": adminPassword },
      });

      if (sessionId !== adminSessionIdRef.current) return;

      if (response.status === 401 || response.status === 403) {
        rejectAuthorization();
        return;
      }

      if (!response.ok) {
        setError("無法讀取代理商統計，請稍後再試");
        return;
      }

      const data = await response.json() as AgentStatsResponse;

      if (sessionId !== adminSessionIdRef.current) return;

      applyAgentStats(data);
    } catch {
      if (sessionId !== adminSessionIdRef.current) return;
      setError("無法連線到後台 API，請稍後再試");
    } finally {
      if (sessionId === adminSessionIdRef.current) {
        setSummaryLoading(false);
      }
    }
  }, [adminPassword, applyAgentStats, rejectAuthorization]);

  useEffect(() => {
    document.title = "YX GO ADMIN · 代理商管理";
  }, []);

  useEffect(() => {
    if (restorationStartedRef.current) return;
    restorationStartedRef.current = true;

    const savedPassword = readSessionPassword();
    const savedLastActivity = readSessionLastActivity();

    clearSessionPassword();
    clearSessionLastActivity();

    if (!savedPassword) {
      lastActivityRef.current = 0;
      return;
    }

    if (!savedLastActivity) {
      clearAdminSession(INVALID_SESSION_MESSAGE);
      return;
    }

    if (Date.now() - savedLastActivity >= IDLE_TIMEOUT_MS) {
      clearAdminSession(IDLE_LOGOUT_MESSAGE);
      return;
    }

    void authenticate(savedPassword, savedLastActivity);
  }, [authenticate, clearAdminSession]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const markActivity = () => {
      if (!isAuthenticatedRef.current) return;

      const timestamp = Date.now();
      lastActivityRef.current = timestamp;
      saveSessionLastActivity(timestamp);
    };
    const checkForIdle = () => {
      const lastActivity = lastActivityRef.current || readSessionLastActivity();

      if (!lastActivity) {
        clearAdminSession(INVALID_SESSION_MESSAGE);
        return;
      }

      if (Date.now() - lastActivity >= IDLE_TIMEOUT_MS) {
        clearAdminSession(IDLE_LOGOUT_MESSAGE);
      }
    };
    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") {
        checkForIdle();
      }
    };

    checkForIdle();

    if (!lastActivityRef.current) return;

    const intervalId = window.setInterval(checkForIdle, IDLE_CHECK_INTERVAL_MS);
    window.addEventListener("click", markActivity, { passive: true });
    window.addEventListener("pointerdown", markActivity, { passive: true });
    window.addEventListener("touchstart", markActivity, { passive: true });
    window.addEventListener("keydown", markActivity);
    window.addEventListener("scroll", markActivity, { passive: true, capture: true });
    window.addEventListener("focus", checkForIdle);
    document.addEventListener("visibilitychange", checkWhenVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("click", markActivity);
      window.removeEventListener("pointerdown", markActivity);
      window.removeEventListener("touchstart", markActivity);
      window.removeEventListener("keydown", markActivity);
      window.removeEventListener("scroll", markActivity, true);
      window.removeEventListener("focus", checkForIdle);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };
  }, [clearAdminSession, isAuthenticated]);

  const submitLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isVerifying) return;

    const candidatePassword = passwordInput;

    if (!candidatePassword) {
      setError("請輸入管理密碼");
      return;
    }

    void authenticate(candidatePassword);
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMemberSearch(memberSearchInput.trim());
  };

  const exportCsv = () => {
    if (exportClaims.length === 0) return;

    const headers = [
      "代理商編號",
      "NFC 卡片編號",
      "會員帳號",
      "輪盤點數",
      "領獎序號",
      "狀態",
      "申請時間",
      "處理時間",
      "客服備註",
    ];
    const rows = exportClaims.map(({ agent, claim }) => [
      agent.id,
      agent.card.id,
      claim.memberAccount,
      claim.prizePoints,
      claim.claimId,
      STATUS_LABELS[claim.status],
      formatTaiwanTime(claim.createdAt),
      claim.processedAt ? formatTaiwanTime(claim.processedAt) : "",
      claim.notes ?? "",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(csvCell).join(","))
      .join("\r\n");
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `914go-claims-${taiwanDateKey(new Date().toISOString())}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const logout = () => {
    clearAdminSession("");
  };

  const toggleAgentMembers = (agentId: AgentId) => {
    setError("");
    setExpandedClaimId(null);

    if (window.matchMedia("(max-width: 768px)").matches) {
      const verticalPosition = window.scrollY;

      document.querySelectorAll<HTMLElement>(".admin-table-scroll").forEach((element) => {
        element.scrollLeft = 0;
      });
      window.scrollTo({ left: 0, top: verticalPosition, behavior: "auto" });
    }

    if (selectedAgentId === agentId) {
      setSelectedAgentId(null);
      return;
    }

    setStatusFilter("all");
    setMemberSearchInput("");
    setMemberSearch("");
    setSelectedAgentId(agentId);
  };

  const toggleAgentMembersOnMobile = (
    event: MouseEvent<HTMLTableRowElement>,
    agentId: AgentId,
  ) => {
    if (!window.matchMedia("(max-width: 768px)").matches) return;

    const target = event.target;

    if (!(target instanceof Element) || target.closest("button, a, input, select, textarea")) {
      return;
    }

    toggleAgentMembers(agentId);
  };

  const toggleClaimDetailsOnMobile = (
    event: MouseEvent<HTMLTableRowElement>,
    claimId: string,
  ) => {
    if (!window.matchMedia("(max-width: 768px)").matches) return;

    const target = event.target;

    if (!(target instanceof Element) || target.closest("button, a, input, select, textarea")) {
      return;
    }

    setExpandedClaimId((current) => current === claimId ? null : claimId);
  };

  const updateClaim = async (claimId: string, status: ClaimStatus) => {
    if (updatingClaimId) return;

    const sessionId = adminSessionIdRef.current;
    setUpdatingClaimId(claimId);
    setError("");

    try {
      const response = await fetch(`/api/admin/claims/${encodeURIComponent(claimId)}`, {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          "X-Admin-Password": adminPassword,
        },
        body: JSON.stringify({
          status,
          notes: notesDrafts[claimId] ?? "",
        }),
      });

      if (
        sessionId !== adminSessionIdRef.current
        || !isAuthenticatedRef.current
      ) return;

      if (response.status === 401) {
        rejectAuthorization();
        return;
      }

      if (!response.ok) {
        setError("更新失敗，請重新嘗試");
        return;
      }

      await loadAgentStats();
    } catch {
      setError("無法連線到後台 API，請稍後再試");
    } finally {
      setUpdatingClaimId(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <main className="admin-shell admin-login-shell">
        <section className="admin-login-card">
          <div className="admin-brand-mark">
            <img src="/media/yx9935/branding/yx-ui-logo.svg" alt="YX" />
          </div>
          <p className="admin-eyebrow">CUSTOMER SERVICE</p>
          <h1>代理商管理</h1>
          <p className="admin-muted">請輸入管理密碼後繼續。</p>
          <form className="admin-login-form" onSubmit={submitLogin}>
            <label htmlFor="admin-password">管理密碼</label>
            <input
              id="admin-password"
              type="password"
              value={passwordInput}
              onChange={(event) => setPasswordInput(event.target.value)}
              autoComplete="current-password"
              autoFocus
            />
            {error && <p className="admin-error" role="alert">{error}</p>}
            <button type="submit" disabled={isVerifying}>
              {isVerifying ? "驗證中" : "登入後台"}
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div>
          <p className="admin-eyebrow">YX GO ADMIN</p>
          <h1>代理商管理</h1>
        </div>
        <button className="admin-ghost-button" type="button" onClick={logout}>登出</button>
      </header>

      <section className="admin-overview" aria-labelledby="admin-overview-title">
        <div className="admin-section-heading">
          <div>
            <p className="admin-eyebrow">OVERVIEW</p>
            <h2 id="admin-overview-title">代理商總覽</h2>
          </div>
          {summaryLoading && <span>統計讀取中…</span>}
        </div>
        <div className="admin-overview-grid">
          <div><span>代理商總數</span><strong>{appConfig.nfcAgents.length}</strong></div>
          <div><span>全部申請會員數</span><strong>{overview.applicantCount}</strong></div>
          <div><span>全部待處理數</span><strong>{overview.pendingCount}</strong></div>
          <div><span>全部已完成數</span><strong>{overview.completedCount}</strong></div>
          <div><span>全部已送優惠點數</span><strong>{overview.completedPoints}</strong></div>
        </div>
      </section>

      <section className="admin-export-panel" aria-labelledby="admin-export-title">
        <div className="admin-section-heading">
          <div>
            <p className="admin-eyebrow">CSV EXPORT</p>
            <h2 id="admin-export-title">日期篩選與 CSV 匯出</h2>
          </div>
          <span>匯出符合下列條件的會員領獎紀錄</span>
        </div>
        <div className="admin-export-filters">
          <label>
            <span>開始日期</span>
            <input
              type="date"
              value={exportStartDate}
              onChange={(event) => setExportStartDate(event.target.value)}
            />
          </label>
          <label>
            <span>結束日期</span>
            <input
              type="date"
              value={exportEndDate}
              onChange={(event) => setExportEndDate(event.target.value)}
            />
          </label>
          <label>
            <span>代理商</span>
            <select
              value={exportAgentId}
              onChange={(event) => setExportAgentId(event.target.value as ExportAgentFilter)}
            >
              <option value="all">全部</option>
              {appConfig.nfcAgents.map((agent) => (
                <option value={agent.id} key={agent.id}>{agent.id}</option>
              ))}
            </select>
          </label>
          <label>
            <span>狀態</span>
            <select
              value={exportStatus}
              onChange={(event) => setExportStatus(event.target.value as StatusFilter)}
            >
              <option value="all">全部</option>
              <option value="pending">待處理</option>
              <option value="completed">已完成</option>
              <option value="rejected">已拒絕</option>
            </select>
          </label>
          <button
            className="admin-export-button"
            type="button"
            onClick={exportCsv}
            disabled={summaryLoading || exportClaims.length === 0}
          >匯出 CSV</button>
        </div>
        <p className={`admin-export-result${exportClaims.length === 0 ? " admin-export-result-empty" : ""}`}>
          {summaryLoading
            ? "資料讀取中…"
            : exportClaims.length === 0
              ? "目前沒有可匯出的資料"
              : `目前可匯出 ${exportClaims.length} 筆資料`}
        </p>
      </section>

      {error && <p className="admin-page-error" role="alert">{error}</p>}

      <section className="admin-agents-section" aria-labelledby="admin-agents-title">
        <div className="admin-section-heading">
          <div>
            <p className="admin-eyebrow">AGENTS</p>
            <h2 id="admin-agents-title">代理商帳號</h2>
          </div>
          <span>固定顯示 {appConfig.nfcAgents.length} 個啟用中的代理商</span>
        </div>
        <p className="admin-stats-note">
          申請會員數是已在本系統提交領獎申請的不重複會員帳號，不代表 YX 真實註冊人數；已送優惠點數只統計客服標記為已完成的紀錄。
        </p>

        <div className="admin-table-scroll admin-agent-table-scroll">
          <table className="admin-data-table admin-agents-table">
            <thead>
              <tr>
                <th>代理商編號</th>
                <th>NFC 卡片編號</th>
                <th>申請會員數</th>
                <th>待處理數</th>
                <th>已完成數</th>
                <th>已拒絕數</th>
                <th>已送優惠點數</th>
                <th>狀態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
          {appConfig.nfcAgents.map((agent) => {
            const stats = agentStats[agent.id] ?? emptyAgentStats(agent.id);
            const isSelected = selectedAgentId === agent.id;
            const normalizedSearch = memberSearch.toLocaleLowerCase();
            const allClaims = stats.claims ?? [];
            const visibleClaims = allClaims.filter((claim) => (
              (statusFilter === "all" || claim.status === statusFilter)
              && (!normalizedSearch || claim.memberAccount.toLocaleLowerCase().includes(normalizedSearch))
            ));

            return (
              <Fragment key={agent.id}>
                <tr
                  className={`admin-agent-row${isSelected ? " admin-agent-row-selected" : ""}`}
                  onClick={(event) => toggleAgentMembersOnMobile(event, agent.id)}
                >
                  <td>
                    <strong className="admin-agent-id">{agent.id}</strong>
                    <span className="admin-mobile-expand-hint" aria-hidden="true">
                      {isSelected ? "收合 ▲" : "展開 ▼"}
                    </span>
                    <div className="admin-mobile-agent-summary">
                      <div className="admin-mobile-agent-primary">
                        <strong>{agent.id}</strong>
                        <span>申請 {stats.applicantCount}</span>
                        <span className="admin-agent-status">啟用中</span>
                        <span className="admin-mobile-agent-toggle">{isSelected ? "收合 ▲" : "展開 ▼"}</span>
                      </div>
                      <div className="admin-mobile-agent-secondary">
                        <span>{agent.card.id}</span>
                        <span>待處理 {stats.pendingCount}</span>
                        <span>完成 {stats.completedCount}</span>
                        <span>已送 {stats.completedPoints} 點</span>
                      </div>
                    </div>
                  </td>
                  <td>{agent.card.id}</td>
                  <td className="admin-number-cell">{stats.applicantCount}</td>
                  <td className="admin-number-cell">{stats.pendingCount}</td>
                  <td className="admin-number-cell">{stats.completedCount}</td>
                  <td className="admin-number-cell">{stats.rejectedCount}</td>
                  <td className="admin-number-cell">{stats.completedPoints}</td>
                  <td><span className="admin-agent-status">啟用中</span></td>
                  <td>
                    <button
                      className="admin-table-action-button"
                      type="button"
                      aria-expanded={isSelected}
                      onClick={() => toggleAgentMembers(agent.id)}
                    >{isSelected ? "收合會員" : "查看會員"}</button>
                  </td>
                </tr>

                {isSelected && (
                  <tr className="admin-expanded-row">
                    <td colSpan={9}>
                    <section className="admin-agent-members" aria-labelledby={`admin-members-${agent.id}`}>
                    <div className="admin-section-heading admin-members-heading">
                      <div>
                        <p className="admin-eyebrow">MEMBER CLAIMS</p>
                        <h2 id={`admin-members-${agent.id}`}>{agent.id} 會員領獎紀錄</h2>
                      </div>
                      <span>只顯示此代理商資料</span>
                    </div>

                    <section className="admin-toolbar" aria-label={`${agent.id} 會員領獎資料篩選`}>
                      <label>
                        <span>狀態</span>
                        <select
                          value={statusFilter}
                          onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                        >
                          <option value="all">全部</option>
                          <option value="pending">待處理</option>
                          <option value="completed">已完成</option>
                          <option value="rejected">已拒絕</option>
                        </select>
                      </label>
                      <form className="admin-search-form" onSubmit={submitSearch}>
                        <label htmlFor={`member-search-${agent.id}`}>會員帳號搜尋</label>
                        <div>
                          <input
                            id={`member-search-${agent.id}`}
                            type="search"
                            value={memberSearchInput}
                            onChange={(event) => setMemberSearchInput(event.target.value)}
                            placeholder="輸入完整或部分帳號"
                          />
                          <button type="submit">搜尋</button>
                        </div>
                      </form>
                      <button
                        className="admin-refresh-button"
                        type="button"
                        onClick={() => void loadAgentStats()}
                        disabled={summaryLoading}
                      >{summaryLoading ? "讀取中…" : "重新整理"}</button>
                    </section>

                    <div className="admin-list-heading">
                      <h2>會員紀錄</h2>
                      <span>顯示 {visibleClaims.length} 筆，共 {allClaims.length} 筆</span>
                    </div>

                    {summaryLoading && allClaims.length === 0 ? (
                      <div className="admin-empty">資料讀取中…</div>
                    ) : allClaims.length === 0 ? (
                      <div className="admin-empty">目前沒有會員資料</div>
                    ) : visibleClaims.length === 0 ? (
                      <div className="admin-empty">目前沒有符合條件的會員資料</div>
                    ) : (
                      <div className="admin-table-scroll admin-member-table-scroll">
                        <table className="admin-data-table admin-members-table">
                          <thead>
                            <tr>
                              <th>會員帳號</th>
                              <th>領獎序號</th>
                              <th>輪盤點數</th>
                              <th>申請時間</th>
                              <th>狀態</th>
                              <th>客服備註</th>
                              <th>操作</th>
                            </tr>
                          </thead>
                          <tbody>
                        {visibleClaims.map((claim) => {
                          const isUpdating = updatingClaimId === claim.claimId;
                          const isExpanded = expandedClaimId === claim.claimId;
                          const displayedMemberAccount = maskMemberAccountForDisplay(claim.memberAccount);

                          return (
                            <tr
                              className={`admin-member-row${isExpanded ? " admin-member-row-expanded" : ""}`}
                              key={claim.claimId}
                              aria-expanded={isExpanded}
                              onClick={(event) => toggleClaimDetailsOnMobile(event, claim.claimId)}
                            >
                              <td><strong className="admin-member-account">{displayedMemberAccount}</strong></td>
                              <td className="admin-claim-id-cell">{claim.claimId}</td>
                              <td className="admin-number-cell">{claim.prizePoints} 點</td>
                              <td className="admin-time-cell">{formatTaiwanTime(claim.createdAt)}</td>
                              <td>
                                <span className={`admin-status admin-status-${claim.status}`}>
                                  {STATUS_LABELS[claim.status]}
                                </span>
                              </td>
                              <td>
                                <input
                                  className="admin-notes-input"
                                  type="text"
                                  aria-label={`${displayedMemberAccount} 客服備註`}
                                  value={notesDrafts[claim.claimId] ?? ""}
                                  onChange={(event) => setNotesDrafts((current) => ({
                                    ...current,
                                    [claim.claimId]: event.target.value,
                                  }))}
                                  placeholder="輸入處理備註"
                                />
                              </td>
                              <td>
                              <div className="admin-row-actions">
                                {claim.status !== "pending" && (
                                  <button
                                    className="admin-pending-button"
                                    type="button"
                                    disabled={Boolean(updatingClaimId)}
                                    onClick={() => void updateClaim(claim.claimId, "pending")}
                                  >改回待處理</button>
                                )}
                                <button
                                  className="admin-complete-button"
                                  type="button"
                                  disabled={Boolean(updatingClaimId)}
                                  onClick={() => void updateClaim(claim.claimId, "completed")}
                                >{isUpdating ? "更新中…" : "標記已完成"}</button>
                                <button
                                  className="admin-reject-button"
                                  type="button"
                                  disabled={Boolean(updatingClaimId)}
                                  onClick={() => void updateClaim(claim.claimId, "rejected")}
                                >{isUpdating ? "更新中…" : "標記已拒絕"}</button>
                              </div>
                              </td>
                            </tr>
                          );
                        })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    </section>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
